import { useMutation, useQueryClient } from '@tanstack/react-query';
import { apiFetch } from '../client';

interface CheckoutResponse {
  url: string;
  session_id?: string | null;
}

/**
 * Open a Stripe Checkout session for a subscription plan or top-up pack.
 * planKey maps to a per-plan env var (see service/routes_billing.py).
 */
export function useCreateCheckout() {
  return useMutation({
    mutationFn: (planKey: string) =>
      apiFetch<CheckoutResponse>('/api/billing/checkout', {
        method: 'POST',
        body: JSON.stringify({ plan_key: planKey }),
      }),
  });
}

/**
 * Open a Stripe Customer Portal session so the user can manage / cancel.
 * Backend 400s if the user has no stripe_customer_id yet.
 */
export function useCreatePortal() {
  return useMutation({
    mutationFn: () =>
      apiFetch<CheckoutResponse>('/api/billing/portal', { method: 'POST' }),
  });
}

/**
 * Invalidate billing + usage queries — call after returning from Stripe portal
 * or success page so the UI reflects the new plan state quickly.
 */
export function useRefreshBilling() {
  const qc = useQueryClient();
  return () => {
    qc.invalidateQueries({ queryKey: ['billing-status'] });
    qc.invalidateQueries({ queryKey: ['usage'] });
  };
}
